'use client';

import { useInject } from '@actr/di';
import { IconChevronDown } from '@actr/icons/chevron-down';
import { IconSearch } from '@actr/icons/search';
import { SeoTravelPointsStore } from '@actr/stores/src/stores/stores/seo/travel-points';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Button, ButtonSizes, ButtonTypes } from '@actr/ui/src/components/_atoms/button';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { FormInput, FormInputType } from '@actr/ui/src/components/_form/form-input';
import { InputSize } from '@actr/ui/src/components/_form/form-input/specs';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { Link } from '@actr/ui/src/components/link';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';

import * as Styled from './style.css';

export enum PointType {
  COUNTRY = 'country',
  CITY = 'city',
  HOTEL = 'hotel',
  AIRLINES = 'airlines',
}

export type TravelPointsListProps = {
  id: string;
  title: string;
  description: string;
  type: PointType;
  pointsList: {
    [key: string]: [
      {
        name: string;
        route: string;
      }
    ];
  };
};

const TravelPointsList: React.FC<TravelPointsListProps> = observer(
  ({ id, title, description, type, pointsList }) => {
    const travelPointsStore = useInject(SeoTravelPointsStore);
    const { letters: lettersFromStore, selectedLetter: selectedLetterFromStore, filteredPoints } = travelPointsStore;
    const [isSearchCleaned, setIsSearchCleaned] = useState(false);
    const [isMoreButtonClicked, setIsMoreButtonClicked] = useState(false);
    const letters = useMemo(
      () => lettersFromStore?.length ? lettersFromStore : Object.keys(pointsList),
      [lettersFromStore, pointsList, filteredPoints]
    );
    const selectedLetter = useMemo(() => selectedLetterFromStore || Object.keys(pointsList)[0], [selectedLetterFromStore, pointsList]);

    function getPlaceholderName(): string {
      switch (type) {
        case PointType.CITY:
          return 'города';
          break;
        case PointType.COUNTRY:
          return 'страны';
          break;
        case PointType.HOTEL:
          return 'отеля';
          break;
        case PointType.AIRLINES:
          return 'или iata авиакомпании';
          break;
        default:
          return 'места';
      }
    }

    function getPointsList(letter?: string) {
      const requiredChunk = letter
        ? pointsList[letter].slice(0, 10)
        : filteredPoints?.slice(0, 10) ?? [];
      const additionalChunk = letter
        ? pointsList[letter].slice(10, pointsList[letter].length)
        : filteredPoints?.slice(
          10,
          filteredPoints.length
        ) ?? [];
      return (
        <>
          {requiredChunk.map((point, index) => (
            <Link font="paragraph.s.semi" href={`/${point.route}`} key={index}>
              {point.name}
            </Link>
          ))}
          {!!additionalChunk.length && !isMoreButtonClicked && (
            <Button
              className={Styled.ShowMoreButton}
              size={ButtonSizes.S}
              buttonType={ButtonTypes.TERTIARY}
              rightIcons={<IconChevronDown />}
              onClick={handleMorePoints}>
              Ещё
            </Button>
          )}
          { !!additionalChunk.length && additionalChunk.map((point, index) => (
            <Link
              className={Styled.Link({ visible: isMoreButtonClicked })}
              font="paragraph.s.semi"
              href={`/${point.route}`}
              key={index}>
              {point.name}
            </Link>
          ))}
        </>
      );
    }

    const handleInputChange = debounce((_value: string) => {
      setIsSearchCleaned(false);
      setIsMoreButtonClicked(false);
      if (type === PointType.AIRLINES) {
        travelPointsStore.filterAllPoints(_value, true);
      } else {
        travelPointsStore.filterPoints(_value);
      }
    }, 500);

    function handleInputFocus() {
      setIsSearchCleaned(false);
    }

    function handleLetterClick(letter: string) {
      if (letter !== selectedLetter) {
        travelPointsStore.cleanPointsSearch();
        setIsMoreButtonClicked(false);
        setIsSearchCleaned(true);
      }
      travelPointsStore.setSelectedLetter(letter);
    }

    function handleMorePoints() {
      setIsMoreButtonClicked(true);
    }

    return (
      <ContentBlock className={Styled.PointsContent} id={id}>
        <div>
          <Text tagName="h3" font="title.x2l">
            {title}
          </Text>
          <Space size={Sizes.X2S} />
          {!!description && (
            <Text tagName="p" font="paragraph.m">
              {description}
            </Text>
          )}
        </div>

        <FormInput
          className={Styled.FilterPointInput}
          name="search"
          size={InputSize.S}
          dataId={`${type}-search`}
          type={FormInputType.TEXT}
          leftIcon={<IconSearch />}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={`Название ${getPlaceholderName()}`}
          value={isSearchCleaned ? '' : undefined}
        />
        <div className={Styled.AlphabeticalSelector}>
          {letters.map((letter, index) => (
            <Button
              className={Styled.Letter({ isSelected: letter === selectedLetter })}
              key={index}
              size={ButtonSizes.M}
              onClick={() => handleLetterClick(letter)}
              buttonType={ButtonTypes.TERTIARY}>
              {letter.toUpperCase()}
            </Button>
          ))}
        </div>
        <div className={Styled.AlphabeticalTabs}>
          {!!filteredPoints?.length && (
            <div className={Styled.SearchResultsTab}>{getPointsList()}</div>
          )}
          {letters.map((letter, index) => (
            <div
              className={Styled.Tab({ isSelected: letter === selectedLetter && !filteredPoints?.length })}
              key={index}>
              {getPointsList(letter)}
            </div>
          ))}
        </div>
      </ContentBlock>
    );
  }
);

export default TravelPointsList;
